<template>
  <div class="result-page">
    <div class="result-inner">
      <router-link to="/pointe/dashboard" class="back-link">← Dashboard</router-link>

      <div v-if="loading" class="loading-wrap">
        <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
      </div>

      <div v-else-if="!application" class="not-found">
        <p>Application not found.</p>
      </div>

      <div v-else>
        <!-- Approved result -->
        <div v-if="application.overallResult === 'approved'" class="result-hero approved">
          <div class="result-icon">🩰</div>
          <h1>Congratulations!</h1>
          <p class="result-subtitle">You've been approved for the <strong>{{ application.cohortName }}</strong> En Pointe Cohort.</p>

          <div v-if="cohort" class="cohort-fee-card">
            <h3>Next Step: Join the Cohort</h3>
            <div class="fee-row">
              <span>Cohort Fee</span>
              <span>${{ cohort.cohortFee }}</span>
            </div>
            <div v-if="application.memberDiscountApplied" class="fee-row discount">
              <span>Member Discount (25%)</span>
              <span>-${{ memberDiscount }}</span>
            </div>
            <div class="fee-row total">
              <span>Total</span>
              <span>${{ finalCohortFee }}</span>
            </div>
            <p v-if="application.memberDiscountApplied" class="member-note">Your Broche Ballet member discount has been applied.</p>
            <button class="btn-pay" :disabled="payingCohortFee" @click="payCohortFee">
              {{ payingCohortFee ? 'Redirecting…' : 'Pay Cohort Fee & Join →' }}
            </button>
            <p v-if="payError" class="error-text">{{ payError }}</p>
          </div>
        </div>

        <!-- Needs work result -->
        <div v-else-if="application.overallResult === 'needs_work'" class="result-hero needs-work">
          <div class="result-icon">💪</div>
          <h1>Keep Working!</h1>
          <p class="result-subtitle">Here's what to focus on for the <strong>{{ application.cohortName }}</strong> cohort.</p>
        </div>

        <!-- Awaiting result -->
        <div v-else class="result-hero pending">
          <div class="result-icon">⏳</div>
          <h1>Under Review</h1>
          <p class="result-subtitle">We're reviewing your application for <strong>{{ application.cohortName }}</strong>. You'll receive an email when results are ready.</p>
        </div>

        <!-- Criteria list -->
        <div v-if="criteriaWithResults.length" class="criteria-section">
          <h2>Criteria Review</h2>

          <div v-for="c in criteriaWithResults" :key="c.id" class="criterion-result-row">
            <div class="criterion-status">
              <span v-if="c.result === true" class="status-pass">✓</span>
              <span v-else-if="c.result === false" class="status-fail">✗</span>
              <span v-else class="status-pending">—</span>
            </div>
            <div class="criterion-content">
              <div class="criterion-title" :class="{ 'title-fail': c.result === false }">{{ c.title }}</div>
              <div v-if="c.category" class="criterion-cat">{{ c.category }}</div>
              <div v-if="c.adminNotes" class="criterion-notes">{{ c.adminNotes }}</div>

              <!-- Resource links shown for failing criteria -->
              <div v-if="c.result === false && c.resourceLinks && c.resourceLinks.length" class="resource-links">
                <p class="resource-label">Resources to help:</p>
                <div class="link-list">
                  <a v-for="link in c.resourceLinks" :key="link.url" :href="link.url" target="_blank" class="resource-link">
                    {{ link.label }} →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Needs work CTAs -->
        <div v-if="application.overallResult === 'needs_work'" class="next-steps-card">
          <h3>What's Next?</h3>
          <div class="cta-list">
            <div class="cta-item">
              <h4>Apply Your Fee Toward a Foot 360 Consultation</h4>
              <p>Your application fee can be applied toward a full Foot 360 Consultation with personalized guidance and a practice plan.</p>
              <a href="mailto:hello@brocheballet.com?subject=Apply%20my%20fee%20toward%20a%20Foot%20360%20Consultation" class="btn-outline">Contact Us →</a>
            </div>
            <div class="cta-item">
              <h4>Join the Waitlist for the Next Cohort</h4>
              <p>We open applications twice a year. Be the first to know when the next cohort opens.</p>
              <div class="convertkit-embed" ref="convertkitEmbed"></div>
            </div>
          </div>
        </div>

        <!-- Self-assessment link -->
        <div class="self-assess-cta">
          <p>Want to keep track of your progress on each criterion?</p>
          <router-link to="/pointe/self-assessment" class="btn-outline">Browse All Criteria →</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  props: {
    id: { type: String, required: true }
  },
  data () {
    return {
      application: null,
      cohort: null,
      criteria: [],
      loading: true,
      payingCohortFee: false,
      payError: ''
    }
  },
  computed: {
    memberDiscount () {
      if (!this.cohort || !this.application || !this.application.memberDiscountApplied) return 0
      return Math.round(this.cohort.cohortFee * 0.25 * 100) / 100
    },
    finalCohortFee () {
      if (!this.cohort) return 0
      return this.application && this.application.memberDiscountApplied
        ? Math.round((this.cohort.cohortFee - this.memberDiscount) * 100) / 100
        : this.cohort.cohortFee
    },
    criteriaWithResults () {
      const appCriteria = this.application ? (this.application.criteria || {}) : {}
      return this.criteria.map(c => ({
        ...c,
        result: appCriteria[c.id] !== undefined ? appCriteria[c.id].passed : null,
        adminNotes: appCriteria[c.id] ? appCriteria[c.id].adminNotes : ''
      }))
    }
  },
  async created () {
    this.$store.commit('setLayout', 'pointe-layout')
    const [appDoc, criteriaSnap] = await Promise.all([
      db.collection('pointe-applications').doc(this.id).get(),
      db.collection('pointe-criteria').where('active', '==', true).orderBy('order').get()
    ])
    if (appDoc.exists) {
      this.application = { id: appDoc.id, ...appDoc.data() }
      if (this.application.cohortId) {
        const cohortDoc = await db.collection('pointe-cohorts').doc(this.application.cohortId).get()
        if (cohortDoc.exists) {
          this.cohort = { id: cohortDoc.id, ...cohortDoc.data() }
        }
      }
    }
    this.criteria = criteriaSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.loading = false
    if (this.application && this.application.overallResult === 'needs_work') {
      this.$nextTick(() => {
        const container = this.$refs.convertkitEmbed
        if (!container) return
        const script = document.createElement('script')
        script.async = true
        script.setAttribute('data-uid', '0166d3e2a8')
        script.src = 'https://broche-ballet.kit.com/0166d3e2a8/index.js'
        container.appendChild(script)
      })
    }
  },
  methods: {
    async payCohortFee () {
      this.payingCohortFee = true
      this.payError = ''
      try {
        const successUrl = window.location.origin + '/pointe/dashboard?cohort_paid=1'
        const cancelUrl = window.location.href
        const res = await fetch('https://us-central1-broche-ballet-online.cloudfunctions.net/createPointeCohortFeeCheckoutSession', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            applicationId: this.id,
            cohortId: this.application.cohortId,
            amount: this.finalCohortFee,
            successUrl,
            cancelUrl
          })
        })
        const data = await res.json()
        if (data.url) {
          window.location.href = data.url
        } else {
          this.payError = 'Failed to start checkout. Please try again.'
        }
      } catch (e) {
        this.payError = e.message
      }
      this.payingCohortFee = false
    }
  }
}
</script>

<style scoped>
.result-page {
  min-height: 80vh;
  background: #faf9f7;
  font-family: 'Fanwood Text', Georgia, serif;
  padding: 40px 24px;
}

.result-inner { max-width: 720px; margin: 0 auto; }

.back-link { font-size: 13px; color: #888; text-decoration: none; display: block; margin-bottom: 24px; }
.back-link:hover { color: #1a1a1a; }

.loading-wrap { padding: 60px; text-align: center; }
.not-found { padding: 40px; text-align: center; color: #888; }

/* Result heroes */
.result-hero {
  border-radius: 4px;
  padding: 36px;
  text-align: center;
  margin-bottom: 32px;
}

.result-hero.approved { background: #e8f5e9; }
.result-hero.needs-work { background: #fce4ec; }
.result-hero.pending { background: #fff8e1; }

.result-icon { font-size: 52px; margin-bottom: 12px; }

.result-hero h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 34px;
  margin: 0 0 10px;
  color: #1a1a1a;
}

.result-subtitle { font-size: 17px; color: #555; margin: 0; line-height: 1.5; }

/* Cohort fee card */
.cohort-fee-card {
  background: #fff;
  border-radius: 4px;
  padding: 24px;
  margin-top: 24px;
  text-align: left;
}

.cohort-fee-card h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  color: #1a1a1a;
  margin: 0 0 14px;
}

.fee-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0eeec;
  font-size: 15px;
}

.fee-row.discount { color: #2e7d32; }
.fee-row.total { font-weight: 600; font-size: 17px; border-bottom: none; }

.member-note { font-size: 13px; color: #2e7d32; margin: 8px 0 16px; font-style: italic; }

.btn-pay {
  display: block;
  width: 100%;
  background: #1a1a1a;
  color: #fff;
  border: none;
  padding: 14px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 16px;
  cursor: pointer;
  border-radius: 2px;
  margin-top: 16px;
  text-align: center;
}

.btn-pay:hover { background: #333; }
.btn-pay:disabled { opacity: 0.5; cursor: default; }

/* Criteria */
.criteria-section {
  background: #fff;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
}

.criteria-section h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 22px;
  color: #1a1a1a;
  margin: 0 0 16px;
}

.criterion-result-row {
  display: flex;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid #f5f4f2;
}

.criterion-result-row:last-child { border-bottom: none; }

.criterion-status { width: 32px; text-align: center; padding-top: 2px; flex-shrink: 0; }

.status-pass { color: #2e7d32; font-size: 20px; font-weight: 700; }
.status-fail { color: #c62828; font-size: 20px; font-weight: 700; }
.status-pending { color: #bbb; font-size: 18px; }

.criterion-content { flex: 1; }

.criterion-title { font-size: 16px; font-weight: 600; color: #1a1a1a; margin-bottom: 2px; }
.title-fail { color: #c62828; }
.criterion-cat { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 4px; }
.criterion-notes { font-size: 14px; color: #555; line-height: 1.5; margin-top: 4px; }

.resource-links { margin-top: 10px; }
.resource-label { font-size: 12px; text-transform: uppercase; letter-spacing: .06em; color: #888; margin: 0 0 6px; }
.link-list { display: flex; gap: 8px; flex-wrap: wrap; }

.resource-link {
  font-size: 13px;
  color: #1a1a1a;
  text-decoration: none;
  background: #f5f4f2;
  padding: 4px 12px;
  border-radius: 20px;
}

.resource-link:hover { background: #e8e5e1; }

/* Needs work CTAs */
.next-steps-card {
  background: #fff;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
}

.next-steps-card h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 20px;
  color: #1a1a1a;
  margin: 0 0 16px;
}

.cta-list { display: flex; flex-direction: column; gap: 24px; }

.cta-item h4 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 17px;
  color: #1a1a1a;
  margin: 0 0 8px;
}

.cta-item p { font-size: 14px; color: #666; line-height: 1.6; margin: 0 0 12px; }

.convertkit-embed { margin-top: 12px; }

/* Self-assess */
.self-assess-cta {
  background: #e8e5e1;
  border-radius: 4px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.self-assess-cta p { font-size: 15px; color: #555; margin: 0; }

.btn-outline {
  display: inline-block;
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 9px 20px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  text-decoration: none;
  border-radius: 2px;
  white-space: nowrap;
}

.btn-outline:hover { background: #1a1a1a; color: #fff; }

.error-text { color: #c00; font-size: 14px; margin-top: 8px; }
</style>
